import React, { useState } from "react";

import ContentHeader from "components/Layout/ContentHeader";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { Input, Select } from "components/UI/Input";
import Modal from "components/UI/Modal";
import Table, { Column } from "components/UI/Table";
import Tabs, { TabItem } from "components/UI/Tabs";
import "pages/AdminPage.scss";

interface Collection {
  id: string;
  name: string;
  type: "general" | "discount";
  productCount: number;
  discountRate?: number;
  isVisible: boolean;
  order: number;
  startDate?: string;
  endDate?: string;
}

const MOCK: Collection[] = [
  {
    id: "1",
    name: "웨딩 에센셜 컬렉션",
    type: "general",
    productCount: 24,
    isVisible: true,
    order: 1,
  },
  { id: "2", name: "봄 신상 모음", type: "general", productCount: 18, isVisible: true, order: 2 },
  { id: "3", name: "인기 선물 세트", type: "general", productCount: 32, isVisible: true, order: 3 },
  {
    id: "4",
    name: "봄 시즌 할인",
    type: "discount",
    productCount: 45,
    discountRate: 15,
    isVisible: true,
    order: 1,
    startDate: "2024-03-01",
    endDate: "2024-05-31",
  },
  {
    id: "5",
    name: "주방 용품 특가",
    type: "discount",
    productCount: 28,
    discountRate: 20,
    isVisible: false,
    order: 2,
    startDate: "2024-03-15",
    endDate: "2024-04-15",
  },
];

const TABS: TabItem[] = [
  { key: "general", label: "일반 컬렉션", count: 3 },
  { key: "discount", label: "할인 컬렉션", count: 2 },
];

const CollectionManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState("general");
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: "", discountRate: "" });

  const filtered = MOCK.filter((c) => c.type === activeTab);

  const columns: Column<Collection>[] = [
    { key: "order", title: "순서", width: "60px" },
    { key: "name", title: "컬렉션명" },
    { key: "productCount", title: "상품 수", width: "80px", render: (v) => `${v}개` },
    ...(activeTab === "discount"
      ? [
          {
            key: "discountRate",
            title: "할인율",
            width: "80px",
            render: (v: number) => `${v}%`,
          } as Column<Collection>,
          {
            key: "startDate",
            title: "기간",
            width: "200px",
            render: (v: string, row: Collection) => `${v} ~ ${row.endDate}`,
          } as Column<Collection>,
        ]
      : []),
    {
      key: "isVisible",
      title: "공개여부",
      width: "90px",
      render: (v) => <Badge variant={v ? "active" : "draft"}>{v ? "공개" : "숨김"}</Badge>,
    },
    {
      key: "actions",
      title: "관리",
      width: "130px",
      render: () => (
        <div style={{ display: "flex", gap: 6 }}>
          <Button size="sm" variant="ghost">
            수정
          </Button>
          <Button size="sm" variant="ghost">
            상품 관리
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="admin-page">
      <ContentHeader
        title="컬렉션 관리"
        description="상품 컬렉션을 생성하고 관리합니다."
        actions={[
          <Button key="add" variant="primary" onClick={() => setShowModal(true)}>
            + 컬렉션 생성
          </Button>,
        ]}
      />

      <div className="admin-page__card">
        <Tabs items={TABS} activeKey={activeTab} onChange={setActiveTab} />
        <Table columns={columns} data={filtered} rowKey="id" />
      </div>

      <Modal
        open={showModal}
        title="컬렉션 생성"
        onClose={() => setShowModal(false)}
        width="440px"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              취소
            </Button>
            <Button variant="primary" onClick={() => setShowModal(false)}>
              생성
            </Button>
          </>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Select
            label="컬렉션 유형"
            options={[
              { value: "general", label: "일반 컬렉션" },
              { value: "discount", label: "할인 컬렉션" },
            ]}
          />
          <Input label="컬렉션명" placeholder="컬렉션 이름 입력" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
          {activeTab === "discount" && (
            <>
              <Input label="할인율 (%)" type="number" placeholder="15" value={form.discountRate} onChange={(e) => setForm((p) => ({ ...p, discountRate: e.target.value }))} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Input label="시작일" type="date" />
                <Input label="종료일" type="date" />
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default CollectionManagement;
